import { motion } from "framer-motion";
import {
  Home, Zap, ShieldCheck, Wallet, MousePointerClick, FileCheck2, KeyRound,
  ArrowRight,
} from "lucide-react";

const highlights = [
  {
    icon: Zap,
    title: "Close in 5 days",
    desc: "Lightning-fast funding — access your equity in under a week.",
  },
  {
    icon: Wallet,
    title: "$0 out of pocket",
    desc: "Origination fees roll into the loan. Zero closing costs at signing.",
  },
  {
    icon: Home,
    title: "Keep your low rate",
    desc: "Standalone 2nd/3rd lien — never touch your existing mortgage.",
  },
  {
    icon: ShieldCheck,
    title: "Soft credit check",
    desc: "Pre-qualify in minutes with zero impact to your credit score.",
  },
];

const stats = [
  { k: "$25K – $750K", v: "Loan amount" },
  { k: "10 / 15 / 20 / 30 yr", v: "Flexible terms" },
  { k: "Up to 85%", v: "Combined LTV" },
  { k: "600+", v: "Min FICO" },
];

const steps = [
  { icon: MousePointerClick, title: "Apply online", desc: "5-minute application on any device." },
  { icon: ShieldCheck, title: "Soft credit check", desc: "Instant pre-qualification, zero score impact." },
  { icon: FileCheck2, title: "Verify (automated)", desc: "ID + income verified via secure bank connect." },
  { icon: KeyRound, title: "eNotary & fund", desc: "Sign online or in person. Money in your account." },
];

export default function Heloc({ onApply }) {
  return (
    <section
      id="heloc"
      data-testid="heloc-section"
      className="relative py-24 md:py-32 bg-gradient-to-br from-sky-50 via-white to-purple-50/40 overflow-hidden"
    >
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-purple-200/40 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-sky-200/40 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-10 items-end mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-purple-100 border border-purple-200 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-purple-700">
              <Home className="w-3.5 h-3.5" /> Home Equity Line
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-5 tracking-tight text-slate-900 leading-[1.05]">
              Tap your equity{" "}
              <span className="bg-gradient-to-r from-purple-600 via-fuchsia-600 to-sky-500 bg-clip-text text-transparent">
                in a flash.
              </span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-slate-500 leading-relaxed"
          >
            The Rocket Cap HELOC Express lets homeowners unlock up to{" "}
            <span className="font-semibold text-slate-900">$750K</span> in
            equity — without refinancing, without touching your first-mortgage
            rate, and without a dollar out of pocket. Fully online. Funded in
            as few as 5 days.
          </motion.p>
        </div>

        {/* Highlights */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {highlights.map((h, idx) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.06 }}
              data-testid={`heloc-highlight-${idx}`}
              className="rounded-3xl bg-white border border-slate-100 p-6 hover:border-purple-200 hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 grid place-items-center shadow-lg shadow-purple-500/30 mb-4">
                <h.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900">{h.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{h.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Two-col: stats + process */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* At a glance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl bg-white border border-slate-100 p-8 shadow-xl"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-bold text-slate-900">
                HELOC Express — at a glance
              </h3>
              <span className="text-[10px] uppercase tracking-widest text-purple-600 font-bold rounded-full bg-purple-50 border border-purple-100 px-3 py-1">
                Fundrock powered
              </span>
            </div>
            <div className="grid grid-cols-2 gap-5">
              {stats.map((s) => (
                <div key={s.v} className="border-l-2 border-purple-500 pl-4">
                  <p className="font-display text-xl md:text-2xl font-black text-slate-900">
                    {s.k}
                  </p>
                  <p className="text-[11px] uppercase tracking-widest text-slate-400 mt-1 font-semibold">
                    {s.v}
                  </p>
                </div>
              ))}
            </div>

            <ul className="mt-7 space-y-2 text-sm text-slate-600">
              {[
                "Up to 3rd-lien position — never touch your low first-mortgage rate",
                "0.25% rate discount with autopay enrollment",
                "Available across 32 states, including single-family, condos, PUDs, 2-4 unit",
                "Draw periods let you tap additional funds as needs evolve",
              ].map((li) => (
                <li key={li} className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />
                  <span>{li}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Process */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-3xl bg-gradient-to-br from-[#0b1121] to-[#312e81] text-white p-8 shadow-xl relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-purple-500/30 blur-3xl" />
            <h3 className="font-display text-xl font-bold relative">From apply to funded — 5 days</h3>
            <p className="text-sm text-white/60 mt-1 relative">
              100% online. Four steps. Real humans on standby.
            </p>
            <ol className="mt-6 space-y-4 relative">
              {steps.map((s, i) => (
                <li key={s.title} className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/10 backdrop-blur border border-white/20 grid place-items-center text-purple-300 font-bold">
                    {i + 1}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <s.icon className="w-4 h-4 text-purple-300" />
                      <p className="font-display font-bold">{s.title}</p>
                    </div>
                    <p className="text-sm text-white/60 mt-0.5">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </motion.div>
        </div>

        {/* CTA row */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <button
            data-testid="heloc-apply-cta"
            onClick={() => onApply?.({ funding_purpose: "HELOC Express (Home Equity)" })}
            className="inline-flex items-center gap-2 rounded-full bg-[#0b1121] hover:bg-[#1e1b4b] text-white font-bold px-8 py-4 text-sm tracking-[0.15em] uppercase shadow-xl hover:-translate-y-0.5 transition-all"
          >
            Check my HELOC options
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-400 max-w-2xl mx-auto">
          Program details subject to underwriting. Product may vary by state.
          NY & TX have restrictions on secondary/investment properties. Not
          available for co-ops, manufactured housing, or properties over 20
          acres.
        </p>
      </div>
    </section>
  );
}
